import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Cpu, Watch, Shirt, Home as HomeIcon, Plane, Briefcase, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { TrustBadges } from "@/components/TrustBadges";
import { ComingSoonBadge } from "@/components/ComingSoonBadge";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/context/CurrencyContext";

const ICONS = { cpu: Cpu, watch: Watch, shirt: Shirt, home: HomeIcon, plane: Plane, briefcase: Briefcase };

export default function Home() {
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const { region } = useCurrency();

  useEffect(() => {
    api.get("/products?trending=true&limit=8").then(({ data }) => setTrending(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0">
          <img src="/logo.png" alt="" className="h-full w-full object-cover opacity-25 blur-sm" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08090E] via-[#08090E]/85 to-[#08090E]/70" />
          <div className="absolute left-1/2 top-1/3 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-[#1a6bff]/20 blur-[130px]" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-[#00F0FF]">
              <Sparkles className="h-3.5 w-3.5" />
              {region?.country ? `Shipping fast to ${region.country}` : "Worldwide fulfilment network"}
            </div>
            <img src="/logo-clean.png" alt="Drop Worldwide" className="animate-floaty mx-auto h-40 w-auto object-contain sm:h-52 lg:h-64" data-testid="hero-logo" />
            <p className="mx-auto mt-6 max-w-xl text-base text-slate-300 sm:text-lg">
              Curated global tech, wearables & lifestyle gear from a worldwide supplier network — with buyer protection and localized pricing.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/shop">
                <Button className="rounded-full bg-[#00F0FF] px-7 py-6 text-base font-bold text-[#08090E] hover:bg-[#33F3FF] glow-cyan" data-testid="hero-shop-btn">
                  Explore Products <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/track">
                <Button variant="outline" className="rounded-full border-white/20 bg-white/5 px-7 py-6 text-base text-slate-100 hover:bg-white/10" data-testid="hero-track-btn">
                  Track an Order
                </Button>
              </Link>
            </div>
            {stats && (
              <div className="mt-12 flex flex-wrap justify-center gap-10">
                {[["190+", "Countries"], [`${(stats.orders / 1000).toFixed(0)}k+`, "Orders shipped"], [`${stats.rating}★`, "Avg rating"]].map(([n, l]) => (
                  <div key={l}><div className="font-display text-2xl font-bold text-white">{n}</div><div className="text-xs uppercase tracking-wider text-slate-400">{l}</div></div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8"><TrustBadges /></section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#00F0FF]">Browse</p>
            <h2 className="font-display mt-1 text-2xl font-bold text-white sm:text-3xl">Shop by Category</h2>
          </div>
          <Link to="/shop" className="hidden items-center gap-1 text-sm text-slate-300 hover:text-[#00F0FF] sm:flex">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {categories.map((c, i) => {
            const Icon = ICONS[c.icon] || Cpu;
            return (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={`/shop?category=${c.slug}`} data-testid={`category-${c.slug}`} className="dw-card group flex flex-col items-center gap-3 rounded-2xl p-6 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#00F0FF]/10 text-[#00F0FF] transition-transform group-hover:scale-110"><Icon className="h-6 w-6" /></div>
                  <div><p className="text-sm font-semibold text-slate-100">{c.name}</p><p className="mt-0.5 font-mono text-[10px] text-slate-500">{c.count} items</p></div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Trending */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#00F0FF] flex items-center gap-2"><Zap className="h-3.5 w-3.5" /> Hot right now</p>
            <h2 className="font-display mt-1 text-2xl font-bold text-white sm:text-3xl">Trending Worldwide</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {trending.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="dw-card relative overflow-hidden rounded-3xl p-10 text-center lg:p-16">
          <div className="absolute inset-0 bg-gradient-to-r from-[#00F0FF]/10 via-transparent to-[#8A2BE2]/10" />
          <div className="relative">
            <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">Launching a global catalog you'll love</h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-300">Full product stories and pricing are dropping soon. Create an account to be first in line and unlock member perks.</p>
            <Link to="/account"><Button className="mt-8 rounded-full bg-[#00F0FF] px-8 py-6 text-base font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="cta-account-btn">Create Free Account</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
